'use client'

import { cn } from '@/utilities/ui'
import React, { useEffect, useRef, useState } from 'react'

type Props = {
  src: string
  poster?: string
  className?: string
  /** Keep the native controls (e.g. showcase videos). Decorative loops pass false. */
  controls?: boolean
}

/**
 * Autoplay-when-visible video. The `src` is only attached once the element scrolls
 * near the viewport, so the (often multi-MB) file is never downloaded on initial load.
 * Preserves the muted/looping autoplay UX while keeping it off the critical path.
 */
export const LazyVideo: React.FC<Props> = ({ src, poster, className, controls = false }) => {
  const ref = useRef<HTMLVideoElement>(null)
  const [inView, setInView] = useState(false)

  useEffect(() => {
    const el = ref.current
    if (!el || inView) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setInView(true)
          observer.disconnect()
        }
      },
      { rootMargin: '300px' },
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [inView])

  return (
    <video
      ref={ref}
      className={cn(className)}
      autoPlay
      loop
      muted
      playsInline
      controls={controls}
      preload="none"
      poster={poster || undefined}
      {...(inView ? { src } : {})}
    >
      Your browser does not support the video tag.
    </video>
  )
}
